// ---------------------------------------------------------------------------
// Meta Pixel loader + guarded event wrapper.
//
// The pixel is NEVER hardcoded in index.html. It loads only when the consent
// manager reports marketing is granted, and every event routes through
// `trackPixel()` which no-ops unless the pixel is live. This is what keeps the
// page free of any Meta code until (and unless) the visitor allows it.
//
// The pixel only ever sees marketing/site-usage signals — page paths and the
// fact that a signup/checkout/purchase happened. It never receives idea, memo,
// or session content. Do not pass idea text, titles, or memo bodies into any
// event params here.
//
// Network load is gated to production builds. In dev/preview the consent flow
// and events still run (logged to the console) so the UX is testable, but no
// real request is made to Meta — that keeps dev traffic out of the ad account.
// ---------------------------------------------------------------------------

import {
  isMarketingGranted,
  subscribeConsent,
} from './consentManager';

/**
 * The Meta Pixel this site reports to. Confirmed in Events Manager as the
 * pixel the ad account actually uses.
 *
 * BEWARE: a SECOND pixel (`1158604970673123`) also exists in the same Business
 * Portfolio, and Meta support has referred to that one as the site's pixel. It
 * is not. Anything that must line up with browser events — most importantly a
 * Conversions API dataset and its access token — has to be created under the ID
 * below, or server events land on a pixel the browser never talks to,
 * deduplication silently fails, and campaigns optimize on half the picture.
 *
 * If you are about to change this value, confirm in Events Manager which pixel
 * the live ad account is attached to first. Pointing the site at the wrong
 * pixel costs every conversion signal without producing a single error.
 */
const PIXEL_ID = '1013898844827529';
const IS_PROD = import.meta.env.PROD;

type PixelParams = Record<string, string | number | boolean>;

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean };
    _fbq?: unknown;
  }
}

let scriptInjected = false;
let initialized = false;
/** The injected <script>, held so revocation can remove it. */
let injectedScript: HTMLScriptElement | null = null;

/**
 * Inject the Meta fbevents script and initialize the pixel. Idempotent — the
 * first call wires the standard `fbq` stub and loads the library; later calls
 * are no-ops. Only ever called once consent is granted.
 */
function loadPixelScript(): void {
  if (scriptInjected || typeof window === 'undefined') return;
  scriptInjected = true;

  // Standard Meta bootstrap stub — queues calls until fbevents.js loads.
  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    // Held at module scope so consent withdrawal can remove it.
    injectedScript = t;
    const s = b.getElementsByTagName(e)[0];
    // Meta's own snippet assumes a <script> already exists in the document and
    // silently does nothing when it doesn't (their `s.parentNode?.` swallows
    // it). Same fix as `linkedinTag.ts`: fall back to <head>.
    if (s?.parentNode) s.parentNode.insertBefore(t, s);
    else b.head.appendChild(t);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

/**
 * Ensure the pixel is loaded + initialized. Safe to call repeatedly. In dev
 * it marks itself initialized without a network load so events log cleanly.
 */
function ensureInitialized(): void {
  if (initialized) return;

  if (!IS_PROD) {
    // Dev/preview: don't touch the network or the real ad account.
    initialized = true;
    console.info('[meta-pixel] (dev) init', PIXEL_ID);
    return;
  }

  loadPixelScript();
  // We send every SPA PageView ourselves (route effect in App.tsx). Turn off the
  // pixel's own history listener so it can never report a URL we didn't vet,
  // such as a shared memo link reached by an in-app navigation.
  (window.fbq as unknown as { disablePushState?: boolean } | undefined) &&
    ((window.fbq as unknown as { disablePushState?: boolean }).disablePushState = true);
  window.fbq?.('init', PIXEL_ID);
  initialized = true;
}

/**
 * The one guarded entry point for every pixel event. No-ops entirely unless
 * marketing consent is granted, then lazily loads + inits the pixel on first
 * allowed event and forwards the call.
 */
export function trackPixel(
  event: string,
  params?: PixelParams,
  /**
   * Shared id for deduplication against the same event sent server-side. Meta
   * collapses a browser and a server event into one when the event NAME and this
   * id both match. Omitting it on an event the server also sends means the
   * conversion is counted twice, which silently doubles reported ROAS.
   */
  eventId?: string,
): void {
  if (!isMarketingGranted()) return;
  ensureInitialized();

  if (!IS_PROD) {
    console.info('[meta-pixel] (dev) track', event, params ?? '', eventId ?? '');
    return;
  }
  window.fbq?.('track', event, params, eventId ? { eventID: eventId } : undefined);
}

/**
 * Match signals for a server-side event, read out of the browser.
 *
 * `_fbp` and `_fbc` are set by the pixel and readable only here, and they are
 * the difference between a well-matched server event and a barely-matched one.
 * Returns an empty object when marketing consent is absent, so a declining
 * visitor's identifiers are never forwarded anywhere.
 */
export function capiSignals(eventId?: string): {
  fbp?: string;
  fbc?: string;
  liFatId?: string;
  rdtCid?: string;
  eventId?: string;
  sourceUrl?: string;
  granted?: boolean;
} {
  // Returns nothing at all without consent. The `granted` flag below is sent
  // explicitly anyway, so the server's privacy guarantee does not depend on
  // this early return continuing to exist.
  if (!isMarketingGranted()) return {};
  const read = (name: string): string | undefined =>
    document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${name}=`))
      ?.slice(name.length + 1);

  // LinkedIn's li_fat_id arrives two ways and both matter. The cookie only
  // exists once the Insight Tag has loaded, which needs consent AND a moment;
  // the URL parameter is present on the very first request of an ad click. On a
  // visitor who signs up immediately, the parameter is often the only copy
  // available, so it is read as a fallback. Also stashed at app start
  // (`captureLiFatIdFromUrl`) so it survives navigation away from the landing
  // URL.
  const liFatId =
    read('li_fat_id') ??
    new URLSearchParams(window.location.search).get('li_fat_id') ??
    sessionStorage.getItem('ed_li_fat_id') ??
    undefined;

  // Reddit's click id, `rdt_cid`. Like `li_fat_id` it arrives as a URL
  // parameter on the ad click and is gone by the time a signup or lead happens
  // several routes later, so it is stashed at app start (`captureRdtCidFromUrl`)
  // and read back here. It is the strongest match signal Reddit's Conversions
  // API has for an anonymous visitor, and the one server events exist to carry.
  const rdtCid =
    new URLSearchParams(window.location.search).get('rdt_cid') ??
    sessionStorage.getItem('ed_rdt_cid') ??
    undefined;

  return {
    ...(read('_fbp') ? { fbp: read('_fbp') } : {}),
    ...(read('_fbc') ? { fbc: read('_fbc') } : {}),
    ...(liFatId ? { liFatId } : {}),
    ...(rdtCid ? { rdtCid } : {}),
    ...(eventId ? { eventId } : {}),
    sourceUrl: window.location.href,
    granted: true,
  };
}

/**
 * Stash `li_fat_id` from the landing URL for the rest of the session.
 *
 * An ad click lands with `?li_fat_id=...`, but signup happens several routes
 * later, by which point the parameter is long gone from the URL. Without this,
 * the strongest LinkedIn match signal is lost for exactly the visitors who came
 * from a LinkedIn ad — the only ones it exists for.
 *
 * `sessionStorage`, not `localStorage`: this is per-visit attribution data, not
 * a durable preference, and it should not outlive the tab.
 */
export function captureLiFatIdFromUrl(): void {
  if (typeof window === 'undefined') return;
  const fromUrl = new URLSearchParams(window.location.search).get('li_fat_id');
  if (fromUrl) sessionStorage.setItem('ed_li_fat_id', fromUrl);
}

/**
 * Stash Reddit's `rdt_cid` click id from the landing URL for the rest of the
 * session — the exact counterpart to `captureLiFatIdFromUrl`. A Reddit ad click
 * lands with `?rdt_cid=...`, but the conversion (a lead or signup) happens
 * several routes later, by which point the parameter is gone. Without this, the
 * strongest Reddit match signal is lost for precisely the visitors it exists
 * for — the ones who came from a Reddit ad.
 *
 * `sessionStorage`, not `localStorage`: per-visit attribution, not a durable
 * preference, and it should not outlive the tab.
 */
export function captureRdtCidFromUrl(): void {
  if (typeof window === 'undefined') return;
  const fromUrl = new URLSearchParams(window.location.search).get('rdt_cid');
  if (fromUrl) sessionStorage.setItem('ed_rdt_cid', fromUrl);
}

/**
 * Id shared between a browser event and its server twin. Generated per
 * conversion, never reused — a reused id would make Meta discard the second
 * conversion as a duplicate of the first.
 */
export function newEventId(): string {
  return crypto.randomUUID();
}

/** Convenience wrapper for the most common event. */
export function trackPixelPageView(): void {
  trackPixel('PageView');
}

/**
 * Install the pixel lifecycle. Called once at app start:
 *   - If consent is already granted (stored choice, or opt-out region on
 *     first load), initialize immediately and fire the first PageView.
 *   - Otherwise, wait. The moment consent flips to granted (banner accept,
 *     note dismissed as allow, preferences change), initialize and fire the
 *     PageView that the visitor's landing view should have counted.
 *
 * Returns an unsubscribe function (used only in dev/StrictMode cleanup).
 */
export function installPixelLifecycle(): () => void {
  let firedInitialPageView = false;

  const onConsent = () => {
    if (isMarketingGranted()) {
      if (!firedInitialPageView) {
        firedInitialPageView = true;
        ensureInitialized();
        trackPixelPageView();
      }
      return;
    }

    // Withdrawal. Meta is already safe on the event path — every send goes
    // through `trackPixel`, which re-checks consent on each call, so no further
    // event fires regardless. This clears the resident library anyway, for two
    // reasons: it stops Meta's own automatic-event collection, and it means the
    // Cookie preferences screen's promise is kept literally rather than
    // technically. Also re-arms the initial PageView, so a later re-grant
    // behaves like a fresh grant instead of silently skipping it.
    if (initialized) {
      unloadPixel();
      firedInitialPageView = false;
    }
  };

  // Fire now if already allowed at boot.
  onConsent();

  // React to a later change, in either direction.
  return subscribeConsent(onConsent);
}

/**
 * Unload the pixel on consent withdrawal. Best-effort: code already executed
 * cannot be un-run, but no further event is sent, the cookies the pixel sets
 * are expired, and the latches reset so a re-grant loads cleanly.
 */
function unloadPixel(): void {
  if (typeof window === 'undefined') return;

  injectedScript?.remove();
  injectedScript = null;

  delete window.fbq;
  delete window._fbq;

  // `_fbp` is the pixel's own first-party cookie; `_fbc` stores click
  // attribution from an `fbclid` landing.
  expireCookie('_fbp');
  expireCookie('_fbc');

  scriptInjected = false;
  initialized = false;
}

/** Expire a cookie across the plausible path/domain combinations. */
function expireCookie(name: string): void {
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const host = window.location.hostname;
  const parts = host.split('.');
  const registrable = parts.length > 2 ? parts.slice(-2).join('.') : host;
  for (const domain of [undefined, host, `.${host}`, `.${registrable}`]) {
    document.cookie =
      `${name}=; expires=${past}; path=/` + (domain ? `; domain=${domain}` : '');
  }
}
